import React, { FC } from 'react';

import Joi from 'joi';

interface FormInputProps {
  name: string;
  isInvalid?: boolean;
  required?: boolean;
  label: string;
  onChange?: (e: React.ChangeEvent<{ name: string; value: string }>) => void;
  onFocus: (e: React.FocusEvent<{ name: string }>) => void;
}

export interface FormValueInputProps extends FormInputProps {
  value: string;
}

export interface FormCheckedInputProps extends FormInputProps {
  checked: boolean;
}

type NamePath = (string | number)[];

function combinePaths(prefix: NamePath | undefined, name: NamePath): NamePath {
  return prefix ? prefix.concat(name) : name;
}

function pathsEqual(a: NamePath, b: NamePath): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function get(obj: any, path: NamePath): any {
  let result = obj;

  for (let i = 0; i < path.length; i++) {
    if (result == null) break;

    result = result[path[i]];
  }

  return result;
}

function toJoiDescriptionPath(path: NamePath, specifier: NamePath) {
  const result: NamePath = [];
  for (let i = 0; i < path.length; i++) {
    const k = path[i];
    if (typeof k === 'string') {
      result.push('keys', k);
    } else {
      result.push('items', 0);
    }
  }

  result.push(...specifier);

  return result;
}

function propsFor<K extends string, V>(
  name: string,
  valueKey: K,
  defaultValue: V,
): (FormInputProps & { [k in K]: V }) | undefined {
  const context = useFormContext();

  if (!context) return undefined;

  const prefix = context.prefixRef.current;

  const valuePath = combinePaths(prefix, [name]);
  const value = get(context.value, valuePath) ?? defaultValue;
  const isInvalid = context.validationErrors?.some(r => pathsEqual(r.path, valuePath));

  const labelPath = toJoiDescriptionPath(combinePaths(prefix, [name]), ['flags', 'label']);
  const label = get(context.schema, labelPath) ?? name;
  const presencePath = toJoiDescriptionPath(combinePaths(prefix, [name]), ['flags', 'presence']);
  const required = get(context.schema, presencePath) === 'required';

  const pathRef = React.useRef(valuePath);
  pathRef.current = valuePath;

  return {
    name,
    isInvalid: isInvalid && (!context.isNew || context.inputState.isTouched(valuePath)),
    label,
    required,
    onFocus: () => context.inputState.markTouched(context.prefixRef, name),
    [valueKey]: value,
  } as FormInputProps & { [k in K]: V };
}

export function propsForValue(name: string): FormValueInputProps | undefined {
  return propsFor(name, 'value', '');
}

export function propsForChecked(name: string): FormCheckedInputProps | undefined {
  return propsFor(name, 'checked', false);
}

interface FormContext {
  isNew: boolean;
  prefixRef: React.MutableRefObject<NamePath>;
  value: any;
  schema: Joi.Description;
  validationErrors?: Joi.ValidationErrorItem[];
  validationWarnings?: Joi.ValidationErrorItem[];
  validatedValue: any;
  inputState: InputStateManager;
}

type QualifiedFieldReference = [prefix: React.MutableRefObject<NamePath>, name: string];

class InputStateManager {
  private touchedInputs;
  private setTouchedInputs;
  constructor() {
    const [TouchedInputs, setTouchedInputs] = React.useState<QualifiedFieldReference[]>([]);
    this.touchedInputs = TouchedInputs;
    this.setTouchedInputs = setTouchedInputs;
  }

  isTouched(path: NamePath) {
    const prefix = path.slice(0, -1);
    const name = path[path.length - 1];
    return this.touchedInputs.some(([p, n]) => pathsEqual(prefix, p.current) && name === n);
  }

  markTouched(prefix: React.MutableRefObject<NamePath>, name: string) {
    this.setTouchedInputs(cur => {
      if (cur.some(([p, n]) => Object.is(p, prefix) && n === name)) return cur;
      return [...cur, [prefix, name]];
    });
  }

  cleanup(prefix: React.MutableRefObject<NamePath>) {
    this.setTouchedInputs(inputs => {
      if (!inputs.some(([p]) => Object.is(p, prefix))) return inputs;
      return inputs.filter(([p]) => !Object.is(p, prefix));
    });
  }
}

const formContext = React.createContext<FormContext | undefined>(undefined);

function useFormContext() {
  return React.useContext(formContext);
}

type ValidationMessageFilter = (path: NamePath, context: { isTouched: boolean; isNew: boolean }) => boolean;

interface FormValidationMessagesProps {
  filter?: ValidationMessageFilter;
  children: (messages: { errors: string[]; warnings: string[] }) => React.ReactNode;
}

function applyValidationFilter(
  ctx: FormContext,
  filter: ValidationMessageFilter | undefined,
  items: Joi.ValidationErrorItem[] | undefined,
) {
  const validationMessages = [];
  if (items) {
    for (const { path, message } of items) {
      if (!filter || filter(path, { isTouched: ctx.inputState.isTouched(path), isNew: ctx.isNew })) {
        validationMessages.push(message);
      }
    }
  }
  return validationMessages;
}

export const FormValidationMessages: FC<FormValidationMessagesProps> = ({ filter, children }) => (
  <formContext.Consumer>
    {ctx =>
      ctx
        ? children({
            warnings: applyValidationFilter(ctx, filter, ctx.validationWarnings),
            errors: applyValidationFilter(ctx, filter, ctx.validationErrors),
          })
        : null
    }
  </formContext.Consumer>
);

const schemaDescriptionCache = new WeakMap<Joi.Schema, Joi.Description>();
function getSchemaDescription(schema: Joi.Schema) {
  let description = schemaDescriptionCache.get(schema);
  if (!description) {
    description = schema.describe();
    schemaDescriptionCache.set(schema, description);
  }
  return description;
}

export interface FormProps {
  isNew?: boolean;
  schema: Joi.Schema;
  value: any;
  validationContext?: Joi.Context;
}

export const Form: FC<FormProps> = ({ isNew, schema, validationContext, value, children }) => {
  const validation = React.useMemo(() => {
    const {
      error,
      warning,
      value: validatedValue,
    } = schema.validate(value, {
      abortEarly: false,
      allowUnknown: true,
      stripUnknown: true,
      errors: { wrap: { label: false } },
      context: validationContext,
    });

    return {
      value: value,
      validatedValue,
      schema: getSchemaDescription(schema),
      validationErrors: error?.details,
      validationWarnings: warning?.details,
    };
  }, [value, schema, validationContext]);

  const prefixRef = React.useRef<NamePath>([]);
  const inputState = new InputStateManager();

  return (
    <formContext.Provider
      value={{
        ...validation,
        prefixRef,
        isNew: !!isNew,
        inputState,
      }}
    >
      {children}
    </formContext.Provider>
  );
};

interface FormItemProps {
  path: NamePath;
  isNew?: boolean;
}

export const FormItem: FC<FormItemProps> = ({ path, isNew, children }) => {
  const prefixRef = React.useRef(path);

  const context = React.useContext(formContext);
  if (context == null) return null;

  const newContext: FormContext = Object.create(context);
  prefixRef.current = combinePaths(context.prefixRef.current, path);
  newContext.prefixRef = prefixRef;

  if (isNew != null) {
    newContext.isNew = !!isNew;
  }

  React.useEffect(() => () => context.inputState.cleanup(prefixRef), []);

  return <formContext.Provider value={newContext}>{children}</formContext.Provider>;
};

export function useValidatedFormValue(): { isValid: false } | { isValid: true; hasWarning: boolean; value: any } {
  const context = useFormContext();

  if (!context || context.validationErrors?.length) return { isValid: false };

  return {
    isValid: true,
    hasWarning: !!context.validationWarnings?.length,
    value: context.validatedValue,
  };
}

export function useFormValue() {
  const context = useFormContext();
  return {
    isNew: context?.isNew,
    value: context?.value,
  };
}
